import React, { useEffect, useState } from "react";
import { getRequest, putRequest } from "../../../services/api";
import PrimaryButton from "../../../components/button/PrimaryButton";
import SimpleButton from "../../../components/button/SimpleButton";
import { ActionButton } from "../../../components/table/ActionGroup";
import DeleteIcon from "../../../assets/DeleteIcon";
import SectionHeader from "../../../components/section/SectionHeader";
import SectionTitle from "../../../components/section/SectionTitle";
import toast from "react-hot-toast";

const normalizedConfigLabel = (key) =>
  key
    .replace(/[_.]/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const emptyStoreInfo = {
  storeName: "",
  storeAddress: "",
  openingHours: "",
  contactNumber: "",
};

function AdminSettings() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [storeInfo, setStoreInfo] = useState([emptyStoreInfo]);

  useEffect(() => {
    getConfig();

    getStoreInfo();
  }, []);

  async function getConfig() {
    try {
      const data = await getRequest("admin/settings/config", true);
      setConfig(data);
    } catch (err) {
      console.log(err);
    }
  }

  async function getStoreInfo() {
    try {
      const data = await getRequest("admin/settings/config/stores", true);
      console.log(data);
      if (data && data.length > 0) {
        setStoreInfo(JSON.parse(data[0].settingValue));
      }
    } catch (err) {
      console.log(err);
    }
  }

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    toast.loading("Saving config...");
    try {
      const data = await putRequest("admin/settings/config", config, true);
      toast.dismiss();
      toast.success("Config saved successfully");
      setConfig(data);
    } catch (err) {
      setError(err.message);
      toast.dismiss();
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (key, value) => {
    const index = config.findIndex((entry) => entry.settingKey === key);
    if (index !== -1) {
      const updatedConfig = [...config];
      updatedConfig[index].settingValue = value;
      setConfig(updatedConfig);
    }
  };

  const handleSaveStoreInfo = async () => {
    toast.loading("Saving store info...");
    try {
      await putRequest(
        "admin/settings/config/stores",
        { storeInfo: JSON.stringify(storeInfo) },
        true,
      );
      toast.dismiss();
      toast.success("Store info saved successfully");
    } catch (err) {
      toast.dismiss();
      toast.error(err.message);
    }
  };

  return (
    <div>
      {config ? (
        <>
          <div className="bg-white rounded shadow p-6 lg:col-span-2">
            <div className="rounded-t mb-0 pl-2 py-3 pb-8 border-0">
              <SectionHeader>
                <SectionTitle title={"Config"} />
              </SectionHeader>
            </div>

            {error ? (
              <div className="bg-errorBg text-errorText text-sm p-4 mb-6 border-errorBorder border-[0.5px] rounded-sm">
                {error}
              </div>
            ) : null}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {config.map((entry) => (
                <div key={entry.settingKey} className="relative w-full">
                  <label className="block uppercase text-slate-600 text-xs font-bold mb-2">
                    {normalizedConfigLabel(entry.settingKey)}
                  </label>
                  <input
                    type="text"
                    aria-label={entry.settingKey}
                    className="border-0 px-3 py-3 placeholder-slate-300 text-slate-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                    value={entry.settingValue ?? ""}
                    onChange={(e) =>
                      updateConfig(entry.settingKey, e.target.value)
                    }
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-end mt-8">
              <PrimaryButton
                title="Save Config"
                onClick={handleSave}
                isBusy={saving}
              />
            </div>
          </div>

          <div className="bg-white rounded shadow p-6 lg:col-span-2 mt-8">
            <div className="rounded-t mb-0 pl-2 py-3 pb-8 border-0">
              <SectionHeader>
                <SectionTitle title={"Store Address"} />
              </SectionHeader>
            </div>

            <div>
              {storeInfo.map((info, index) => (
                <AddLocationForm
                  key={index}
                  storeInfo={info}
                  canDelete={true}
                  onChange={(updatedInfo) => {
                    const newStoreInfo = [...storeInfo];
                    newStoreInfo[index] = updatedInfo;
                    setStoreInfo(newStoreInfo);
                  }}
                  onDelete={() => {
                    const newStoreInfo = storeInfo.filter(
                      (_, i) => i !== index,
                    );
                    setStoreInfo(newStoreInfo);
                  }}
                />
              ))}

              <div className="flex justify-end mt-8 gap-3">
                <PrimaryButton title="Save" onClick={handleSaveStoreInfo} />
                <SimpleButton
                  title="Add More"
                  bgColor="bg-green-500"
                  textColor="text-white"
                  onClick={() => {
                    setStoreInfo([...storeInfo, emptyStoreInfo]);
                  }}
                />
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

const AddLocationForm = ({ storeInfo, canDelete, onChange, onDelete }) => {
  const handleInputChange = (field, value) => {
    onChange({ ...storeInfo, [field]: value });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 bg-gray-50 p-4 mb-2">
      <div className="relative w-full space-y-1">
        <label className="block uppercase text-slate-600 text-xs font-bold mb-2">
          Store Name
        </label>
        <input
          type="text"
          className="border-0 px-3 py-3 placeholder-slate-300 text-slate-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
          value={storeInfo.storeName || ""}
          onChange={(e) => handleInputChange("storeName", e.target.value)}
        />
      </div>
      <div className="relative w-full space-y-1">
        <label className="block uppercase text-slate-600 text-xs font-bold mb-2">
          Store Address
        </label>
        <textarea
          className="border-0 px-3 py-3 placeholder-slate-300 text-slate-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
          onChange={(e) => handleInputChange("storeAddress", e.target.value)}
        >
          {storeInfo.storeAddress || ""}
        </textarea>
      </div>
      <div className="relative w-full space-y-1">
        <label className="block uppercase text-slate-600 text-xs font-bold mb-2">
          Opening Hours
        </label>
        <input
          type="text"
          className="border-0 px-3 py-3 placeholder-slate-300 text-slate-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
          value={storeInfo.openingHours || ""}
          onChange={(e) => handleInputChange("openingHours", e.target.value)}
        />
      </div>
      <div className="relative w-full space-y-1">
        <label className="block uppercase text-slate-600 text-xs font-bold mb-2">
          Contact Number
        </label>
        <input
          type="text"
          className="border-0 px-3 py-3 placeholder-slate-300 text-slate-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
          value={storeInfo.contactNumber || ""}
          onChange={(e) => handleInputChange("contactNumber", e.target.value)}
        />
      </div>
      {canDelete && (
        <div className="flex items-center justify-end col-span-4">
          <ActionButton color="red" onClick={onDelete} title="Delete">
            <DeleteIcon />
          </ActionButton>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;
